import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import { Box, CircularProgress } from '@mui/material';
import Sidebar from './components/common/Sidebar';
import MainHome from './components/Home';
import './App.css';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { StyledEngineProvider } from '@mui/material/styles';
import ScheduleManagement from './pages/ScheduleManagement';
import FireSafetyInspectionForm from './components/fireSafetyInspection/FireSafetyInspectionForm';
import ServicePreparingPage from './components/common/ServicePreparingPage';
import ProtectedRoute from './components/common/ProtectedRoute';
import AdminInquiryList from './components/inquiry/AdminInquiryList';

// 지연 로딩할 컴포넌트들
const Settings = React.lazy(() => import('./components/settings/Settings'));
const InspectionForm = React.lazy(() => import('./components/inspection/InspectionForm'));
const InspectionResult = React.lazy(() => import('./components/inspection/InspectionResult'));
const InspectionEdit = React.lazy(() => import('./components/inspection/InspectionEdit'));
const SafetyEducation = React.lazy(() => import('./components/safety/SafetyEducation'));
const CompanyList = React.lazy(() => import('./components/company/CompanyList'));
const UserList = React.lazy(() => import('./components/user/UserList'));
const UserManagement = React.lazy(() => import('./pages/UserManagement'));
const CompanyManagement = React.lazy(() => import('./pages/CompanyManagement'));
const InspectionList = React.lazy(() => import('./components/inspection/InspectionList'));
const NoticeList = React.lazy(() => import('./components/notice/NoticeList'));
const InquiryList = React.lazy(() => import('./components/inquiry/InquiryList'));
const InspectionHome = React.lazy(() => import('./components/inspection/InspectionHome'));
const CustomerCenter = React.lazy(() => import('./components/customer/CustomerCenter'));
const FireSafetyInspectionResult = React.lazy(() => import('./components/fireSafetyInspection/FireSafetyInspectionResult'));
const FireSafetyInspectionList = React.lazy(() => import('./components/fireSafetyInspection/FireSafetyInspectionList'));
const FireSafetyInspectionEdit = React.lazy(() => import('./components/fireSafetyInspection/FireSafetyInspectionEdit'));
const OnboardingHome = React.lazy(() => import('./components/tutorial/OnboardingHome'));
const SiteIntroTutorial = React.lazy(() => import('./components/tutorial/SiteIntroTutorial'));
const SignUpTutorial = React.lazy(() => import('./components/tutorial/SignUpTutorial'));
const ElectricalInspectionTutorial = React.lazy(() => import('./components/tutorial/ElectricalInspectionTutorial'));
const FireInspectionTutorial = React.lazy(() => import('./components/tutorial/FireInspectionTutorial'));
const InquiryTutorial = React.lazy(() => import('./components/tutorial/InquiryTutorial'));
const PdfTutorial = React.lazy(() => import('./components/tutorial/PdfTutorial'));
const InspectionResultTransmissionTutorial = React.lazy(() => import('./components/tutorial/InspectionResultTransmissionTutorial'));
const KakaoAlertList = React.lazy(() => import('./components/alert/KakaoAlertList'));
const GuestInquiryList = React.lazy(() => import('./components/inquiry/GuestInquiryList'));
const EmailSendTutorial = React.lazy(() => import('./components/tutorial/EmailSendTutorial'));
const SignTutorial = React.lazy(() => import('./components/tutorial/SignTutorial'));

// 태양광 점검 관련 컴포넌트 로딩
const SolarInspection = React.lazy(() => import('./components/solar/SolarInspection'));
const SolarInspectionList = React.lazy(() => import('./components/solar/SolarInspectionList'));
const SolarInspectionResult = React.lazy(() => import('./components/solar/SolarInspectionResult'));

// 학교 점검 관련 컴포넌트 로딩
const SchoolSafetyEducation = React.lazy(() => import('./components/school/SchoolSsafetyEducation'));
const SchoolInspectionList = React.lazy(() => import('./components/school/SchoolInspectionList'));
const SchoolInspectionResult = React.lazy(() => import('./components/school/SchoolInspectionResult'));
const SchoolInspectionForm = React.lazy(() => import('./components/school/SchoolInspectionForm'));
const SchoolInspectionEdit = React.lazy(() => import('./components/school/SchoolInspectionEdit'));
const SchoolBulkPdfDownload = React.lazy(() => import('./components/school/SchoolBulkPdfDownload'));

// 발전설비 점검 관련 컴포넌트 로딩
const GeneratorInspection = React.lazy(() => import('./components/generator/GeneratorInspection'));
const GeneratorInspectionResult = React.lazy(() => import('./components/generator/GeneratorInspectionResult'));
const GeneratorInspectionList = React.lazy(() => import('./components/generator/GeneratorInspectionList'));

// EV 충전시설 점검 관련 컴포넌트 로딩
const EvChargingInspection = React.lazy(() => import('./components/evCharging/EvChargingInspection'));
const EvChargingInspectionList = React.lazy(() => import('./components/evCharging/EvChargingInspectionList'));
const EvChargingInspectionResult = React.lazy(() => import('./components/evCharging/EvChargingInspectionResult'));

// 점검 양식 선택 페이지 컴포넌트
const InspectionTypeSelection = React.lazy(() => import('./components/inspection/InspectionTypeSelection'));

// 로딩 컴포넌트
const LoadingFallback = () => (
  <Box 
    sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      height: '100vh'
    }}
  >
    <CircularProgress />
  </Box>
);

function App() {
  return (
    <StyledEngineProvider injectFirst>
      <Router>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box
              sx={{
                maxWidth: '430px',
                margin: '0 auto',
                minHeight: '100vh',
                boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
                backgroundColor: '#FFFFFF',
                position: 'relative'
              }}
            >
              <Sidebar />
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route path="/" element={<MainHome />} />
                  <Route path="/inspection" element={<InspectionHome />} />
                  <Route path="/inspection/safety" element={<SafetyEducation />} />
                  <Route path="/inspection/select-type" element={<InspectionTypeSelection />} />
                  <Route path="/inspection/new" element={<InspectionForm />} />
                  <Route path="/inspection/:id" element={<InspectionResult />} />
                  <Route path="/inspection/edit/:id" element={<InspectionEdit />} />
                  <Route path="/companies" element={<CompanyList />} />
                  <Route path="/companies/:companyId" element={<CompanyManagement />} />
                  <Route path="/users" element={<UserList />} />
                  <Route path="/users/:userId" element={<UserManagement />} />
                  <Route path="/inspections" element={<InspectionList />} />
                  <Route path="/notices" element={<NoticeList />} />
                  <Route path="/inquiries" element={<InquiryList />} />
                  <Route path="/settings/*" element={<Settings />}/>
                  <Route path="/customer/*" element={<CustomerCenter />} />
                  <Route path="/schedule-management" element={<ScheduleManagement />} />
                  <Route path="/fire-safety-inspection" element={<FireSafetyInspectionForm />} />
                  <Route path="/fire-safety-inspection/edit/:id" element={<FireSafetyInspectionForm />} />
                  <Route path="/fire-safety-inspection/:id" element={<FireSafetyInspectionResult />} />
                  <Route path="/fire-safety-inspections" element={<FireSafetyInspectionList />} />
                  <Route path="/inspection/facility" element={<ServicePreparingPage />} />
                  <Route path="/inspection/equipment" element={<ServicePreparingPage />} />
                  
                  {/* 태양광 점검 관련 라우트 */}
                  <Route path="/solar" element={<SolarInspectionList />} />
                  <Route path="/solar/inspection/new" element={<SolarInspection />} />
                  <Route path="/solar/inspection/:id" element={<SolarInspection />} />
                  <Route path="/solar-inspections" element={<SolarInspectionList />} />
                  <Route path="/solar-inspections/:id" element={<SolarInspectionResult />} />
                  <Route path="/solar-inspections/edit/:id" element={<SolarInspection isEditMode={true} />} />

                  {/* 학교 점검 관련 라우트 */}
                  <Route path="/school-safety-education" element={<SchoolSafetyEducation />} />
                  <Route path="/school-inspections" element={<SchoolInspectionList />} />
                  <Route path="/school-inspections/:id" element={<SchoolInspectionResult />} />
                  <Route path="/school-inspections/edit/:id" element={<SchoolInspectionEdit />} />
                  <Route path="/school-inspections/new" element={<SchoolInspectionForm />} />
                  <Route path="/school-inspections/bulk-download" element={<SchoolBulkPdfDownload />} />

                  {/* 발전설비 점검 관련 라우트 */}
                  <Route path="/generator/inspection/new" element={<GeneratorInspection />} />
                  <Route path="/generator/inspection/:id" element={<GeneratorInspection />} />
                  <Route path="/generator-inspections" element={<GeneratorInspectionList />} />
                  <Route path="/generator-inspections/:id" element={<GeneratorInspectionResult />} />
                  <Route path="/generator-inspections/edit/:id" element={<GeneratorInspection isEditMode={true} />} />

                  {/* EV 충전시설 점검 관련 라우트 */}
                  <Route path="/ev-charging-inspections" element={<EvChargingInspectionList />} />
                  <Route path="/ev-charging-inspections/new" element={<EvChargingInspection />} />
                  <Route path="/ev-charging-inspections/:id" element={<EvChargingInspectionResult />} />
                  <Route path="/ev-charging-inspections/edit/:id" element={<EvChargingInspection />} />

                  <Route path="/tutorial-onboarding" element={<OnboardingHome />} />
                  <Route path="/tutorial/tutorial-site-intro" element={<SiteIntroTutorial />} />
                  <Route path="/tutorial/signup" element={<SignUpTutorial />} />
                  <Route path="/tutorial/electrical-inspection" element={<ElectricalInspectionTutorial />} />
                  <Route path="/tutorial/fire-inspection" element={<FireInspectionTutorial />} />
                  <Route path="/tutorial/inquiry" element={<InquiryTutorial />} />
                  <Route path="/tutorial/pdf" element={<PdfTutorial />} />
                  <Route path="/tutorial/send-results" element={<InspectionResultTransmissionTutorial />} />
                  <Route path="/kakao-alert-list" element={<KakaoAlertList />} />
                  <Route path="/guest-inquiries" element={<GuestInquiryList />} />
                  <Route path="/tutorial/email-send" element={<EmailSendTutorial />} />
                  <Route path="/tutorial/sign" element={<SignTutorial />} />
                  <Route 
                    path="/admin/inquiries" 
                    element={
                      <ProtectedRoute roleRequired="ADMIN">
                        <AdminInquiryList />
                      </ProtectedRoute>
                    } 
                  />
                </Routes>
              </Suspense>
            </Box>

          </LocalizationProvider>
        </ThemeProvider>
      </Router>
    </StyledEngineProvider>
  );
}

export default App; 